import { useTranslation } from 'react-i18next';
import { Accordion } from '../common';
import { useCalculationStore } from '../../stores/calculationStore';
import { useProductionStore } from '../../stores/productionStore';
import { useCurrency } from '../../lib/utils/currency';
import { useUnitStore, KG_TO_LB } from '../../stores/unitStore';

export function WithholdingTaxCalculations() {
  const { t } = useTranslation();
  const { symbol } = useCurrency();
  const { weightUnit } = useUnitStore();
  const taxes = useCalculationStore((state) => state.taxes);
  const metrics = useCalculationStore((state) => state.metrics);

  if (!taxes || !metrics) {
    return null;
  }

  // Calculate average weight from production data
  const productionStore = useProductionStore.getState();
  const harvestEvents = productionStore.harvestEvents;
  const totalCount = harvestEvents.reduce((sum, event) => sum + event.count, 0);
  const totalWeight = harvestEvents.reduce((sum, event) => sum + (event.totalWeight || 0), 0);
  const averageWeight = totalCount > 0 ? totalWeight / totalCount : 0;

  const formatCurrency = (amount: number): string => {
    return amount.toLocaleString('tr-TR', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const netRevenuePerBird = averageWeight * taxes.netPricePerKg;

  return (
    <Accordion 
      title={
        <div className="flex flex-col items-center justify-center w-full text-center">
          <span className="text-sm mb-1">{t('results.withholdingTax')}</span>
          <span className="text-xl font-bold text-green-600 dark:text-green-400 whitespace-nowrap">
            {formatCurrency(taxes.netAfterWithholding)} {symbol}
          </span>
        </div>
      }
      defaultOpen={false}
    >
      <div className="space-y-3">
        {/* Withholding Tax Amount */}
        <div className="flex justify-between items-center p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
          <span className="text-sm text-gray-700 dark:text-gray-300">
            {t('results.withholdingTaxRate4')}
          </span>
          <span className="text-sm font-semibold text-red-600 dark:text-red-400">
            -{formatCurrency(taxes.withholdingTaxAmount)} {symbol}
          </span>
        </div>

        {/* Net Revenue After Withholding */}
        <div className="flex justify-between items-center p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
          <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t('results.netAfterWithholding')}
          </span>
          <span className="text-base font-bold text-green-600 dark:text-green-400">
            {formatCurrency(taxes.netAfterWithholding)} {symbol}
          </span>
        </div>

        {/* Net Price per kg */}
        <div className="p-3 bg-gray-50 dark:bg-gray-800 rounded-md">
          <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">
            {t('results.netPricePerKgAfterTax')}
          </div>
          <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            {formatCurrency(taxes.netPricePerKg)} {symbol}/{weightUnit}
          </div>
        </div>

        {/* Net Revenue per bird */}
        <div className="p-3 bg-green-50 dark:bg-green-900/20 rounded-md">
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              {t('results.netRevenuePerBirdAfterTax')}
            </span>
            <span className="text-base font-semibold text-green-600 dark:text-green-400">
              {formatCurrency(netRevenuePerBird)} {symbol}
            </span>
          </div>
          <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
            {(weightUnit === 'lb' ? (averageWeight || 0) * KG_TO_LB : (averageWeight || 0)).toFixed(3)} {weightUnit} × {formatCurrency(taxes.netPricePerKg)} {symbol}/{weightUnit}
          </div>
        </div>

        {/* Info */}
        <div className="p-2 bg-yellow-50 dark:bg-yellow-900/20 rounded-md text-xs text-gray-600 dark:text-gray-400">
          <p>
            ⚠️ {t('results.withholdingTaxInfo')}
          </p>
        </div>
      </div>
    </Accordion>
  );
}
